// AssigneeAvatarStack — stack of 1-2 char initial badges for the users assigned
// to a quote. Click "+" to open a popover that lets you assign anyone from the
// users directory. Used near the CWQ ref and inside Staff Notes.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../../lib/api";
import { Plus, X } from "lucide-react";

const PALETTE = [
  "#FF6820", "#2FC8CB", "#F7C270", "#B79AFF", "#EF476F",
  "#06D6A0", "#118AB2", "#FF9F1C", "#9B5DE5", "#00BBF9",
];
function initials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  const first = (parts[0] || "").charAt(0);
  const last  = parts.length > 1 ? (parts[parts.length - 1] || "").charAt(0) : "";
  return (first + last).toUpperCase() || "?";
}
function toneFor(name) {
  if (!name) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = ((h * 31) + name.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

/** Single circular initial badge (also used standalone in Staff Notes) */
export function InitialBadge({ name, size = 24, title, testid, onClick }) {
  const bg = toneFor(name);
  return (
    <span
      title={title || name}
      onClick={onClick}
      data-testid={testid}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: size, height: size, borderRadius: "50%",
        background: bg, color: "#0A0F1C",
        fontFamily: "var(--t2-mono)", fontSize: Math.max(9, size * 0.42), fontWeight: 900,
        lineHeight: 1, letterSpacing: 0,
        border: "1.5px solid rgba(15, 26, 48, 0.9)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
        cursor: onClick ? "pointer" : "default",
        flexShrink: 0,
      }}
    >{initials(name)}</span>
  );
}

/** Compact stacked view + inline +add popover */
export function AssigneeAvatarStack({
  assignees,          // string[] of user names
  primary,            // fallback single name (legacy AccountManager) if assignees empty
  currentUserName,    // for the "Assign to me" quick action
  onChange,           // (nextAssignees: string[]) => void
  size = 26,
  testidPrefix = "assignee",
}) {
  const list = useMemo(() => {
    const arr = Array.isArray(assignees) ? assignees.filter(Boolean) : [];
    if (arr.length) return arr;
    return primary ? [primary] : [];
  }, [assignees, primary]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const anchorRef = useRef(null);

  const loadUsers = useCallback(async () => {
    try { const { data } = await api.get("/users"); setUsers(Array.isArray(data) ? data : []); }
    catch { setUsers([]); }
  }, []);
  useEffect(() => { if (pickerOpen) loadUsers(); }, [pickerOpen, loadUsers]);

  const remove = (n) => onChange(list.filter((x) => x !== n));
  const add    = (n) => {
    if (!n || list.includes(n)) return;
    onChange([...list, n]);
    setPickerOpen(false); setQ("");
  };

  const filtered = q
    ? users.filter((u) => (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()))
    : users.slice(0, 20);

  return (
    <>
      <div
        style={{ display: "inline-flex", alignItems: "center" }}
        data-testid={`${testidPrefix}-stack`}
      >
        {list.map((name, i) => (
          <div key={name} style={{ marginLeft: i === 0 ? 0 : -8 }}>
            <InitialBadge
              name={name}
              size={size}
              title={`${name} — click to remove`}
              testid={`${testidPrefix}-badge-${i}`}
              onClick={onChange ? () => {
                if (window.confirm(`Remove ${name} from this quote's assignees?`)) remove(name);
              } : undefined}
            />
          </div>
        ))}
        {onChange && (
          <button
            ref={anchorRef}
            type="button"
            onClick={() => setPickerOpen(true)}
            style={{
              marginLeft: list.length ? -6 : 0,
              width: size, height: size, borderRadius: "50%",
              background: "var(--t2-surface2)", border: "1.5px dashed var(--t2-teal)",
              color: "var(--t2-teal)", cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
            data-testid={`${testidPrefix}-add`}
            title="Assign teammate"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {pickerOpen && createPortal(
        <div
          onClick={() => setPickerOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}
          data-testid={`${testidPrefix}-picker-overlay`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(420px, 92vw)", background: "#0F1A30",
              border: "1px solid rgba(47, 200, 203, 0.28)", borderRadius: 14, overflow: "hidden",
              boxShadow: "0 30px 80px rgba(0,0,0,.7)",
            }}
            data-testid={`${testidPrefix}-picker`}
          >
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--t2-border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div className="t2-mono" style={{ fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t2-teal)", fontWeight: 800 }}>Assign teammate</div>
              <button onClick={() => setPickerOpen(false)} style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer" }}><X className="w-4 h-4" /></button>
            </div>
            <div style={{ padding: 10 }}>
              <input
                autoFocus
                className="t2-input"
                placeholder="Search users by name or email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid={`${testidPrefix}-picker-search`}
              />
            </div>
            {currentUserName && !list.includes(currentUserName) && (
              <button
                onClick={() => add(currentUserName)}
                className="t2-btn-ghost"
                style={{ margin: "0 10px 8px", width: "calc(100% - 20px)", justifyContent: "flex-start", borderColor: "var(--t2-teal)" }}
                data-testid={`${testidPrefix}-picker-self`}
              >
                <InitialBadge name={currentUserName} size={22} />
                <span style={{ marginLeft: 8 }}>Assign to me ({currentUserName})</span>
              </button>
            )}
            <div style={{ maxHeight: 320, overflowY: "auto", padding: "0 6px 10px" }}>
              {filtered.length === 0 && (
                <div className="t2-mono" style={{ padding: 12, fontSize: 11, color: "var(--t2-grey4)" }}>No users match.</div>
              )}
              {filtered.map((u) => {
                const name = u.name || u.email || "";
                const already = list.includes(name);
                return (
                  <button
                    key={u._id || u.id || u.email || name}
                    onClick={() => !already && add(name)}
                    disabled={already}
                    className="t2-cmd-row"
                    style={{ gridTemplateColumns: "28px 1fr auto", opacity: already ? 0.55 : 1, cursor: already ? "not-allowed" : "pointer", marginBottom: 4 }}
                    data-testid={`${testidPrefix}-picker-option`}
                  >
                    <InitialBadge name={name} size={26} />
                    <div style={{ overflow: "hidden" }}>
                      <div className="t2-cmd-name">{name}</div>
                      {u.email && <div className="t2-cmd-meta">{u.email}</div>}
                    </div>
                    <span className="t2-mono" style={{ fontSize: 10, color: already ? "var(--t2-teal)" : "var(--t2-grey3)" }}>{already ? "on quote" : "assign"}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
